import React from 'react';
import {
  CONTENT_WIDTH_MM, LABEL_WIDTH_MM, COMPACT_OPTIONAL_BLOCKS,
  DENSITIES, columnsFor, periodParts,
} from '../../config/compactCV';

/**
 * Kompakt CV-mall för anbud.
 *
 * Medvetna val:
 *  - Ingen MUI-Typography och inga Chips. <Chip size="small"> är 24 px hög
 *    (6,35 mm) vilket är två textrader för en enda kompetenstagg.
 *  - Allt i pt/mm, inte px – pt är stabilt genom webbläsarnas utskriftsskalning.
 *  - Projektraden innehåller enbart år, projekt, byggherre, roll och
 *    eventuell budget. Beskrivningar visas bara för uppdrag som fällts ut
 *    manuellt – projektinfo dessutom bara med tillvalet påslaget.
 *  - Projektnamn som inte får plats radbryter. De kortas aldrig med ellips –
 *    ett avkortat projektnamn i ett anbud är ett vilseledande dokument.
 *  - Etikettkolumnen är exakt lika bred som årskolumnen plus mellanrummet,
 *    så all löptext delar tabbstopp med projektnamnen. Etiketterna måste
 *    därför hållas under ca elva tecken.
 */

const CSS = `
.cvc-doc {
  --s: 1;
  --lh: 1.28;
  --rp: .4mm;
  --lg: .7mm;
  --cols: 17mm 54mm 48mm 67mm;
  --base:  calc(7.5pt * var(--s));
  --label: calc(6.5pt * var(--s));
  --name:  calc(10pt  * var(--s));
  --title: calc(8pt   * var(--s));
  --foot:  calc(6pt   * var(--s));
  width: ${CONTENT_WIDTH_MM}mm;
  margin: 0 auto;
  background: #fff;
  color: #111;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-size: var(--base);
  line-height: var(--lh);
}
.cvc-doc * { box-sizing: border-box; }

.cvc-dochead {
  display: flex; justify-content: space-between; align-items: center;
  font-size: var(--label); text-transform: uppercase; letter-spacing: .4px;
  color: #555; border-bottom: 1.2pt solid #111;
  padding-bottom: 1.2mm; margin-bottom: 2.5mm;
}
.cvc-logo { height: 8mm; width: auto; object-fit: contain; }
.cvc-dochead strong { color: #111; font-size: var(--base); }
.cvc-doctitle {
  font-size: calc(11pt * var(--s)); font-weight: 700; color: #111;
  letter-spacing: 1px;
}

.cvc-person { clear: both; }
.cvc-person + .cvc-person {
  border-top: .8pt solid #111; padding-top: 3mm; margin-top: 4mm;
}
/* Hela namnraden är fet – namn, titel, ort och telefon. */
.cvc-head {
  display: flex; align-items: baseline; gap: 2mm;
  font-weight: 700;
  break-after: avoid; page-break-after: avoid;
}
.cvc-name {
  font-size: var(--name);
  text-transform: uppercase; letter-spacing: .3px; white-space: nowrap;
}
.cvc-title { font-size: var(--title); }
.cvc-meta {
  margin-left: auto; font-size: var(--label); color: #333; white-space: nowrap;
}
.cvc-sub { color: #333; margin-top: .3mm; }

/* Porträttet flyter i högerkant med texten runt om – kostar noll rader
   så länge personens faktarader är fler än bildens höjd. */
.cvc-photo {
  float: right; width: 20mm; aspect-ratio: 4 / 5; object-fit: cover;
  margin: 0 0 1.5mm 3mm; border-radius: 1px;
}

/* Ingressen ligger i samma vänsterkant som namnet och löper i full bredd –
   den är personens rubriktext, inte ett etikettfält. Marginalen nedåt
   skiljer den tydligt från etikettblocket som följer. */
.cvc-ingress { margin: .8mm 0 2.2mm; }

.cvc-line {
  padding-left: ${LABEL_WIDTH_MM}mm;
  text-indent: -${LABEL_WIDTH_MM}mm;
  margin-top: var(--lg);
}
/*
 * Etiketten sätts mindre än brödtexten, så baslinjejustering (webbläsarens
 * standard) lägger etikettens versaler lägre än textens. vertical-align: top
 * plus en nudge på 1,2 pt ställer versalhöjderna i linje i överkant.
 * Talet är skillnaden i halvledning mellan 6,5 pt och 7,5 pt Roboto.
 */
.cvc-label {
  display: inline-block; width: calc(${LABEL_WIDTH_MM}mm - 2.5mm); text-indent: 0;
  margin-right: 2.5mm;
  font-size: var(--label); font-weight: 700; color: #111;
  text-transform: uppercase; letter-spacing: .2px;
  overflow: hidden;
  vertical-align: top; line-height: 1;
  padding-top: calc(1.2pt * var(--s));
}
.cvc-strong { font-weight: 700; }
.cvc-dim { color: #666; }

.cvc-projects { margin-top: 5mm; clear: both; }
/* Ett uppdrag – raden plus eventuell utfälld text – delas aldrig över en
   sidbrytning. useCompactFit sätter data-tall på ett uppdrag som är högre
   än en hel sida, och då får just det brytas. */
.cvc-assign { break-inside: avoid; page-break-inside: avoid; }
.cvc-assign[data-tall="1"],
.cvc-assign[data-tall="1"] .cvc-note { break-inside: auto; page-break-inside: auto; }
/* Cellerna toppjusteras så att raden växer nedåt när något radbryter.
   Ingen cell får rinna in i grannkolumnen: min-width: 0 låter spåret
   bestämma bredden och overflow-wrap: anywhere är sista skyddet för
   ord som är längre än kolumnen. */
.cvc-row {
  display: grid;
  grid-template-columns: var(--cols);
  column-gap: 2.5mm;
  align-items: start;
  letter-spacing: -.01em;
  padding: var(--rp) 0;
  break-inside: avoid; page-break-inside: avoid;
}
.cvc-row > span { min-width: 0; overflow-wrap: anywhere; }
/* Årtal, uppdrag och byggherre i fetstil – rollkolumnen står kvar i normal
   vikt och blir då den kolumn ögat läser sist. */
.cvc-pname, .cvc-client { font-weight: 700; }
/* Rubrikraden är samma rutnät som uppdragsraderna, så rubrikerna hamnar
   exakt över sina kolumner. Den ersätter en separat PROJEKT-rubrik och
   kostar därför ingen extra rad. */
.cvc-hrow {
  font-size: var(--label); font-weight: 700; color: #111;
  text-transform: uppercase; letter-spacing: .3px;
  border-bottom: .8pt solid #666;
  padding-bottom: .6mm; margin-bottom: .6mm;
  break-after: avoid; page-break-after: avoid;
}
.cvc-zebra { background: #ececec; }
/* Ett intervall med månader (2023/10 – 2024/03) är bredare än kolumnen och
   bryts efter tankstrecket. Varje datum hålls ihop för sig. */
.cvc-yr { font-variant-numeric: tabular-nums; font-weight: 700; }
.cvc-yrpart { white-space: nowrap; }
.cvc-hrow .cvc-yr, .cvc-hrow .cvc-budget { font-weight: 700; }
.cvc-budget { font-variant-numeric: tabular-nums; text-align: right; }

/* Utfälld beskrivning för enstaka framlyfta uppdrag. Startar på samma
   tabbstopp som projektnamnen. */
.cvc-note {
  padding-left: ${LABEL_WIDTH_MM}mm;
  color: #333;
  break-inside: avoid; page-break-inside: avoid;
  padding-bottom: .8mm;
}
/* Clearfix i stället för overflow: hidden – det senare skapar en BFC som
   klipper bort bilden när den dras ut till vänster om textkolumnen. */
.cvc-note::after { content: ''; display: block; clear: both; }
/* Projektinfo och rollbeskrivning som separata stycken med förled. */
.cvc-notepart { margin: 0; }
.cvc-notepart + .cvc-notepart { margin-top: var(--lg); }
.cvc-notelead { font-weight: 700; color: inherit; text-decoration: none; }
/* Mjukvaror som pills på en egen rad under texten. Kantlinjen gör dem
   synliga även när bakgrundsgrafik är avstängd i utskriftsdialogen.
   display: flex skapar en egen yta bredvid uppdragsbilden, så pillsen
   aldrig hamnar under den. */
.cvc-pills {
  display: flex; flex-wrap: wrap; gap: .8mm 1mm;
  margin: 0; padding: 0; list-style: none;
}
.cvc-notepart + .cvc-pills, .cvc-notetext + .cvc-pills { margin-top: var(--lg); }
.cvc-pill {
  font-size: var(--base); line-height: 1.25; color: #222;
  padding: 0 1.4mm; border: .5pt solid #8a8a8a; border-radius: 2mm;
  background: #f2f2f2;
  -webkit-print-color-adjust: exact; print-color-adjust: exact;
  max-width: 100%; overflow-wrap: anywhere;
}

/* Uppdragsbilden dras ut till dokumentets vänsterkant så den linjerar med
   årtalskolumnen, medan beskrivningen flödar bredvid och fortsätter på
   textens tabbstopp under bilden. Bildens höjd absorberas då av texten
   i stället för att läggas till den. */
.cvc-noteimg {
  float: left; width: 30mm; aspect-ratio: 4 / 3; object-fit: cover;
  margin: calc(.3mm + var(--yr-over, 0px)) 2.5mm .5mm -${LABEL_WIDTH_MM}mm;
  border-radius: 1px;
}

/* I ett utfällt uppdrag tar datumet bara en rad av rubrikraden. En andra
   datumrad hänger ned i årskolumnen bredvid texten, så texten alltid börjar
   direkt under rubrikraden. useCompactFit mäter överhänget till --yr-over:
   bilden flyttas ned lika mycket och blocket blir minst så högt. */
.cvc-assign[data-open="1"] > .cvc-row > .cvc-yr { height: calc(var(--base) * var(--lh)); }
.cvc-assign[data-open="1"] > .cvc-note { min-height: calc(var(--yr-over, 0px) + .8mm); }

/* Sidfoten ligger i sidans nedre marginal, se pageFooterCss. På skärmen
   ritar CompactSheet den längst ned på arket. */
.cvc-foot {
  padding-top: 1.2mm; border-top: .5pt solid #ccc;
  font-size: 6pt; color: #666;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  display: flex; justify-content: space-between;
}

/* Skärmmarkeringar – följer aldrig med till utskrift */
@media screen {
  /* Egen flagga – radbrytningsmarkeringen är avstängd som standard och
     styrs separat från saknad roll och AI-ändrad text. */
  .cvc-doc[data-wrapflag="1"] .cvc-row[data-wrapped="1"] {
    box-shadow: inset 2px 0 0 #FF6B00;
  }
  .cvc-doc[data-clickable="1"] .cvc-row { cursor: pointer; }
  .cvc-doc[data-clickable="1"] .cvc-row:hover { background: #e8f0fe; }
  .cvc-doc[data-clickable="1"] .cvc-row[data-expanded="1"] { font-weight: 500; }
  .cvc-doc[data-flag="1"] .cvc-missing {
    background: #ffe9d6; border-radius: 1px;
  }
  .cvc-doc[data-flag="1"] .cvc-edited {
    background: #ffe08a; box-shadow: 0 0 0 1px #e0a800; border-radius: 1px;
  }
}
@media print {
  .cvc-doc { width: auto; }
  .cvc-zebra { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
`;

export function CompactCVStyles() {
  return <style>{CSS}</style>;
}

const cssString = s => `"${String(s || '').replace(/["\\]/g, '\\$&').replace(/\s+/g, ' ')}"`;

/**
 * Sidfoten i utskriften. Den läggs i @page-marginalens rutor, så den hamnar
 * längst ned på varje sida och tar ingen plats från innehållet – ett vanligt
 * element i flödet hamnar bara efter sista uppdraget. Marginalen är 8 mm,
 * vilket rymmer foten med god marginal.
 */
function pageFooterCss(company) {
  const box = `
    font-family: Roboto, Helvetica, Arial, sans-serif; font-size: 6pt; color: #666;
    vertical-align: top; padding-top: 1.2mm; border-top: .5pt solid #ccc;`;
  return `@page {
  @bottom-left { content: ${cssString(company?.name)}; text-align: left; ${box} }
  @bottom-center { content: ${cssString(company?.website)}; text-align: center; ${box} }
  @bottom-right { content: ${cssString(company?.email)}; text-align: right; ${box} }
}`;
}

/** Sidfoten som element, för skärmvyn. */
export function CompactFooter({ company }) {
  return (
    <div className="cvc-foot">
      <span>{company?.name || ''}</span>
      <span>{company?.website || ''}</span>
      <span>{company?.email || ''}</span>
    </div>
  );
}

function Line({ label, children }) {
  if (!children) return null;
  return (
    <div className="cvc-line">
      <span className="cvc-label">{label}</span>
      {children}
    </div>
  );
}

function ToolsLine({ person, settings }) {
  const { toolsExpert, toolsGood, toolsBasic } = person;
  if (!toolsExpert.length && !toolsGood.length && !toolsBasic.length) return null;
  return (
    <div className="cvc-line">
      <span className="cvc-label">IT</span>
      {toolsExpert.length > 0 && (
        <span className="cvc-strong">{toolsExpert.join(' · ')}</span>
      )}
      {toolsExpert.length > 0 && toolsGood.length > 0 && <span> · </span>}
      {toolsGood.length > 0 && <span>{toolsGood.join(' · ')}</span>}
      {settings.includeToolsBasic && toolsBasic.length > 0 && (
        <span className="cvc-dim"> · {toolsBasic.join(' · ')}</span>
      )}
    </div>
  );
}

/** Renderar ett tillval om det är påslaget och personen har data. */
function OptionalLine({ id, person, settings }) {
  if (!settings.optional.includes(id)) return null;

  if (id === 'tools') return <ToolsLine person={person} settings={settings} />;

  if (id === 'certifications') {
    const text = settings.splitLicences ? person.otherCertifications : person.certifications;
    return <Line label={settings.splitLicences ? 'Övrig utb.' : 'Certifikat'}>{text}</Line>;
  }

  const block = COMPACT_OPTIONAL_BLOCKS.find(b => b.id === id);
  return <Line label={block?.short || id}>{person[id]}</Line>;
}

/** Perioden i bitar som får radbryta sinsemellan men aldrig inuti. */
function Period({ period }) {
  const parts = periodParts(period);
  return (
    <span className="cvc-yr">
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && ' '}
          <span className="cvc-yrpart">{part}</span>
        </React.Fragment>
      ))}
    </span>
  );
}

/**
 * Texten i ett utfällt uppdrag. Utan tillvalet är den oförändrad – roll och
 * insats, annars projektbeskrivningen. Med tillvalet visas projektbeskrivningen
 * först och rollbeskrivningen efter, var och en med förled. En del som saknas
 * hoppas över helt. Har uppdraget bara den äldre `description` visas den utan
 * förled, eftersom det inte går att veta vad den beskriver.
 */
function AssignmentNote({ a, settings }) {
  if (!settings.showProjectInfo || (!a.projectInfo && !a.roleNote)) {
    return a.note ? <span className="cvc-notetext">{a.note}</span> : null;
  }
  return (
    <>
      {a.projectInfo && (
        <p className="cvc-notepart">
          <span className="cvc-notelead">Projektet:</span> {a.projectInfo}
        </p>
      )}
      {a.roleNote && (
        <p className="cvc-notepart">
          <span className="cvc-notelead">Uppdraget:</span> {a.roleNote}
        </p>
      )}
    </>
  );
}

/** Mjukvarorna som pills. Ett uppdrag utan mjukvaror ritar ingenting. */
function SoftwarePills({ a, settings }) {
  if (!settings.showSoftware || !a.software.length) return null;
  return (
    <ul className="cvc-pills">
      {a.software.map(name => <li key={name} className="cvc-pill">{name}</li>)}
    </ul>
  );
}

export function CompactPerson({ person, settings, editedFields, onToggleExpand, showBudget }) {
  // Presentationen renderas alltid i sin helhet. maxIngressChars är en budget
  // till AI-nedkortningen, inte en sax – att hugga av mitt i ett ord i ett
  // anbud är precis den tysta truncering mallen ska undvika.
  const ingress = settings.showIngress ? person.ingress || '' : '';

  const wasEdited = key => editedFields?.[`${person.userId}:${key}`];

  const hasPills = a => settings.showSoftware && a.software.length > 0;
  const expandable = a => Boolean(onToggleExpand && (a.note || a.image || hasPills(a)));
  const isOpen = a => a.expanded
    && Boolean(a.note || (settings.showAssignmentImages && a.image) || hasPills(a));

  return (
    <section className="cvc-person">
      {settings.showPhoto && person.photo && (
        <img className="cvc-photo" src={person.photo} alt="" />
      )}

      <div className="cvc-head">
        <span className="cvc-name">{person.name}</span>
        {person.title && <span className="cvc-title">· {person.title}</span>}
        {person.meta && <span className="cvc-meta">{person.meta}</span>}
      </div>

      {person.roleInTender && (
        <div className="cvc-sub">
          <span className="cvc-label">Anbudsroll</span>{person.roleInTender}
        </div>
      )}

      {ingress && (
        <p className={'cvc-ingress' + (wasEdited('ingress') ? ' cvc-edited' : '')}>
          {ingress}
        </p>
      )}

      <OptionalLine id="personal_profile" person={person} settings={settings} />
      <OptionalLine id="industries" person={person} settings={settings} />

      <Line label="Kompetens">{person.competencies}</Line>
      <Line label="Utbildning">{person.education}</Line>
      <Line label="Anställning">{person.employment}</Line>

      {settings.splitLicences && <Line label="Behörighet">{person.licences}</Line>}

      <OptionalLine id="certifications" person={person} settings={settings} />
      <OptionalLine id="tools" person={person} settings={settings} />
      <OptionalLine id="standards" person={person} settings={settings} />

      <Line label="Språk">{person.languages}</Line>

      {person.assignments.length > 0 && (
        <div className="cvc-projects">
          <div className="cvc-row cvc-hrow">
            <span>År</span>
            <span>Uppdrag</span>
            <span>Byggherre/beställare</span>
            <span>Roll i uppdrag</span>
            {showBudget && <span className="cvc-budget">Budget</span>}
          </div>
          {person.assignments.map((a, i) => (
            <div key={a.index}
              className={'cvc-assign' + (settings.zebra && i % 2 === 1 ? ' cvc-zebra' : '')}
              data-open={isOpen(a) ? '1' : '0'}>
              <div
                className="cvc-row"
                data-row={`${person.userId}:${a.index}`}
                data-expanded={a.expanded ? '1' : '0'}
                onClick={expandable(a)
                  ? () => onToggleExpand(person.userId, a.index)
                  : undefined}
                /* title sätts av useCompactFit, som också vet om raden
                   radbryter och kan skriva ihop båda förklaringarna. */
                data-expandable={expandable(a) ? '1' : '0'}
              >
                <Period period={a.period} />
                <span className={'cvc-pname' + (wasEdited(`a${a.index}.name`) ? ' cvc-edited' : '')}>
                  {a.name}
                </span>
                <span className={'cvc-client' + (wasEdited(`a${a.index}.client`) ? ' cvc-edited' : '')}>
                  {a.client}
                </span>
                <span
                  className={
                    a.role
                      ? (wasEdited(`a${a.index}.role`) ? 'cvc-edited' : undefined)
                      : 'cvc-missing'
                  }
                >
                  {a.role || '—'}
                </span>
                {showBudget && <span className="cvc-budget">{a.budget}</span>}
              </div>
              {isOpen(a) && (
                <div className="cvc-note">
                  {settings.showAssignmentImages && a.image && (
                    <img className="cvc-noteimg" src={a.image} alt="" />
                  )}
                  <AssignmentNote a={a} settings={settings} />
                  <SoftwarePills a={a} settings={settings} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Hela dokumentet. `docRef` sätts på den nod som mäts och skrivs ut.
 */
export default function CompactCVDocument({
  docRef, persons, settings, company, docTitle, subTitle,
  editedFields, onToggleExpand, flagIssues = true,
}) {
  // Budgetkolumnen tar plats från de andra, så den ritas bara när den
  // faktiskt har något att visa.
  const showBudget = settings.showBudget
    && persons.some(p => p.assignments.some(a => a.budget));

  const density = DENSITIES[settings.density] || DENSITIES.luftig;
  const c = columnsFor(showBudget);
  const cols = [
    `${c.periodMm}mm`, `${c.nameMm}mm`, `${c.clientMm}mm`, `${c.roleMm}mm`,
    ...(showBudget ? [`${c.budgetMm}mm`] : []),
  ].join(' ');

  return (
    <div className="cvc-doc" ref={docRef} data-flag={flagIssues ? '1' : '0'}
      data-wrapflag={settings.markWrapped ? '1' : '0'}
      data-clickable={onToggleExpand ? '1' : '0'}
      style={{
        '--s': settings.fontScale,
        '--lh': density.lineHeight,
        '--rp': `${density.rowPadMm}mm`,
        '--lg': `${density.gapMm}mm`,
        '--cols': cols,
      }}>
      <CompactCVStyles />
      <style>{pageFooterCss(company)}</style>

      <div className="cvc-dochead">
        {settings.showLogo && company?.logo
          ? <img className="cvc-logo" src={company.logo} alt={company?.name || ''} />
          : <strong>{company?.name || ''}</strong>}
        <span className="cvc-doctitle">
          {docTitle}{subTitle ? ` · ${subTitle}` : ''}
        </span>
        <span>{new Date().toLocaleDateString('sv-SE')}</span>
      </div>

      {persons.map(person => (
        <CompactPerson
          key={person.userId}
          person={person}
          settings={settings}
          editedFields={editedFields}
          onToggleExpand={onToggleExpand}
          showBudget={showBudget}
        />
      ))}
    </div>
  );
}
